import React from 'react';

import { useTranslations } from '@vk-hr-tek/core/hooks';

import { UserRoleEnum } from '@app/types';
import { AppAbilities, Can } from '@app/ability';
import { SystemDropdownItem } from '@app/gen/side_menu';

import {
  customLinkToNavigationItem,
  sidebarIcons,
  systemDropdownToNavigationItem,
  systemLinkToNavigationItem,
} from '../helpers';
import { AbilityProvider } from '../../../AbilityProvider';
import { SidebarItemsWithSubmenu } from '../SidebarItemsWithSubmenu';

interface SidebarSystemDropdownItemProps {
  item: SystemDropdownItem;
  sidebarOpened: boolean;
  isActive: (path: string, href: string) => boolean;
  skeletonLoading: boolean;
  pathname: string;
  onMenuItemClick: (pageTitle: string) => void;
  setSubmenusVisibleItemsCount?: (count: number) => void;
}

export const SidebarSystemDropdownItem = ({
  sidebarOpened,
  item,
  pathname,
  isActive,
  skeletonLoading,
  onMenuItemClick,
  setSubmenusVisibleItemsCount,
}: SidebarSystemDropdownItemProps) => {
  const { t } = useTranslations();

  const { nick, children } = item;

  const { href, parentResource, basename, resource } =
    systemDropdownToNavigationItem(item);

  const isActiveItem = isActive(pathname, href);
  const icon = sidebarIcons()[nick];

  const role = nick.startsWith(UserRoleEnum.Employee)
    ? UserRoleEnum.Employee
    : UserRoleEnum.Company;

  const submenuItems = children.map((childItem) => {
    const link =
      childItem.type === 'custom_link_item'
        ? customLinkToNavigationItem(childItem)
        : systemLinkToNavigationItem(childItem);
    return {
      aqaClass: `aqa_${link.basename}`,
      title: link.title
        ? link.title
        : t(
            `modules.${link.parentResource ?? link.resource}.${
              link.basename
            }.${role}Title`,
          ),
      href: link.href ?? '',
      isCustom: !!link.title,
      resource: link.resource as AppAbilities[1],
    };
  });

  return (
    <AbilityProvider role={role}>
      <Can do="read" on={resource}>
        <SidebarItemsWithSubmenu
          icon={icon}
          title={t(
            `modules.${parentResource ?? resource}.${basename}.${role}Title`,
          )}
          sidebarOpened={sidebarOpened}
          isActive={isActiveItem}
          skeletonLoading={skeletonLoading}
          aqaClass={`aqa_${basename}`}
          role={role}
          onMenuItemClick={onMenuItemClick}
          submenu={submenuItems}
          setSubmenusVisibleItemsCount={setSubmenusVisibleItemsCount}
        />
      </Can>
    </AbilityProvider>
  );
};
